import { FriendsTab } from '@colonist/clientforge';
import { getOnlineStatusActivityText } from '../../../../shared/friends/FriendOnlineStatus.ts';
import { AnalyticEventCategory, AnalyticEventType, GiftMembershipMethod } from '../../../../shared/entity_datas/EntityDatas.ts';
import { UIAjaxWrapper } from '../../../shared/scripts/network/UIAjaxWrapper.ts';
import { UIAPIEndpoints } from '../../../shared/scripts/network/UIAPIEndpoints.ts';
import { CA } from '../analytics/Analytics.ts';
import { doActionIfCanGiftMembership } from '../store/StoreUtils.ts';
import { TimeUtils } from '@colonist/utils';
import { navigateToPage } from '../ui_utils/ClientUtils.ts';
import { UIUtilsFriends } from '../ui_friends/UIUtilsFriends.ts';
import { UIUtilsEjsVariables } from '../UIUtilsEjsVariables.ts';
import { mountUIReactComponent } from '../ui_platform/mountUIReactComponent.tsx';
import { UIViewProfilePopup } from './UIViewProfilePopup.ts';
const PROFILE_FRIENDS_MOUNT_ID = 'profile-friends-tab';
const LAST_ONLINE_MORE_THAN_MONTHS = 4;
export class UIProfileFriendsTableController {
    render() {
        if (this.view == undefined) this.view = mountUIReactComponent(PROFILE_FRIENDS_MOUNT_ID, FriendsTab);
        this.view.render({
            friends: this.buildFriendRows(),
            requests: [
                ...this.userFriendsData.friendRequestsReceived
            ],
            sentRequests: [
                ...this.userFriendsData.friendRequestsSent
            ],
            onUserClick: (userId, username)=>UIViewProfilePopup.init(username),
            onGiftClick: (userId, username)=>UIProfileFriendsTableController.onGiftClick(username),
            onAccept: (userId, username)=>UIProfileFriendsTableController.onAccept(userId, username),
            onDecline: (userId, username)=>UIProfileFriendsTableController.onDecline(userId, username),
            onCancel: (userId)=>void this.onCancel(userId)
        });
    }
    buildFriendRows() {
        const friendsOrderedByLastOnline = [
            ...this.userFriendsData.friends
        ].sort((firstUser, secondUser)=>{
            const firstUserLastOnlineTime = UIProfileFriendsTableController.getFriendLastOnlineTime(firstUser.onlineStatus);
            const secondUserLastOnlineTime = UIProfileFriendsTableController.getFriendLastOnlineTime(secondUser.onlineStatus);
            if (secondUserLastOnlineTime > firstUserLastOnlineTime) return 1;
            return -1;
        });
        const showGift = !UIUtilsEjsVariables.isUserOnDiscordMobile && !UIUtilsEjsVariables.shouldHideStoreOnMobile;
        return friendsOrderedByLastOnline.map((friend)=>{
            const onlineStatus = friend.onlineStatus;
            return {
                userId: friend.userId,
                username: friend.username,
                statusText: UIProfileFriendsTableController.getStatusText(onlineStatus),
                activityText: getOnlineStatusActivityText(onlineStatus.activity),
                showGift: showGift
            };
        });
    }
    static onAccept(userId, username) {
        void UIUtilsFriends.friendRequestResponded({
            userId: userId,
            username: username
        }, true);
    }
    static onDecline(userId, username) {
        void UIUtilsFriends.friendRequestResponded({
            userId: userId,
            username: username
        }, false);
    }
    onFriendRequestResponded(target, isAccepted) {
        const friendRequest = this.findFriendRequest(target);
        if (friendRequest == undefined) return Promise.resolve();
        UIProfileFriendsTableController.removeUserFromList(this.userFriendsData.friendRequestsReceived, friendRequest.userId);
        if (isAccepted) {
            this.userFriendsData.friends.push({
                userId: friendRequest.userId,
                username: friendRequest.username,
                onlineStatus: {
                    isOnline: false
                }
            });
        }
        this.render();
        return Promise.resolve();
    }
    findFriendRequest(target) {
        const friendRequests = this.userFriendsData.friendRequestsReceived;
        if (target.userId != undefined) return friendRequests.find((request)=>request.userId == target.userId);
        return friendRequests.find((request)=>request.username == target.username);
    }
    async onCancel(userId) {
        const friendChangeData = {
            userId: userId
        };
        const response = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiUserProfileRemoveFriend(), undefined, friendChangeData, 'application/json');
        if (response == undefined) return;
        UIProfileFriendsTableController.removeUserFromList(this.userFriendsData.friendRequestsSent, userId);
        this.render();
    }
    static removeUserFromList(users, userIdToRemove) {
        const indexToRemove = users.findIndex((user)=>user.userId == userIdToRemove);
        if (indexToRemove == -1) return;
        users.splice(indexToRemove, 1);
    }
    static onGiftClick(username) {
        const giftButtonClickEventData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.GiftMembershipOverlay,
            floatValue: GiftMembershipMethod.ProfileFriendsTab
        };
        void CA.sendAnalyticEvent(giftButtonClickEventData);
        doActionIfCanGiftMembership(()=>navigateToPage(`/store?username=${username}#gift`, true), username);
    }
    static getFriendLastOnlineTime(friendOnlineStatus) {
        if (friendOnlineStatus.isOnline) return new Date().getTime();
        if (friendOnlineStatus.lastOnlineDate == undefined) return 0;
        return new Date(friendOnlineStatus.lastOnlineDate).getTime();
    }
    static getStatusText(friendOnlineStatus) {
        if (friendOnlineStatus.isOnline) return {
            key: 'strings:profilePage.friends.status.online'
        };
        if (!friendOnlineStatus.lastOnlineDate) return {
            key: 'strings:profilePage.friends.status.offline'
        };
        const deltaDaysHoursMinutes = TimeUtils.getDaysHoursMinutesBetweenDates(new Date(), new Date(friendOnlineStatus.lastOnlineDate));
        const moreThanLastOnlineMonthThreshold = deltaDaysHoursMinutes.days > TimeUtils.DAYS_IN_MONTH * LAST_ONLINE_MORE_THAN_MONTHS;
        if (moreThanLastOnlineMonthThreshold) return {
            key: 'strings:profilePage.friends.status.lastOnlineMoreThan3Months'
        };
        if (deltaDaysHoursMinutes.days > TimeUtils.DAYS_IN_MONTH) {
            const months = Math.floor(deltaDaysHoursMinutes.days / TimeUtils.DAYS_IN_MONTH);
            return UIProfileFriendsTableController.getLastOnlineText(months, 'month');
        }
        if (deltaDaysHoursMinutes.days > 0) return UIProfileFriendsTableController.getLastOnlineText(deltaDaysHoursMinutes.days, 'day');
        if (deltaDaysHoursMinutes.hours > 0) return UIProfileFriendsTableController.getLastOnlineText(deltaDaysHoursMinutes.hours, 'hour');
        return UIProfileFriendsTableController.getLastOnlineText(deltaDaysHoursMinutes.minutes, 'minute');
    }
    static getLastOnlineText(count, unit) {
        return {
            key: 'strings:profilePage.friends.status.lastOnline',
            options: {
                context: unit,
                count: -count
            }
        };
    }
    constructor(userFriendsData){
        this.userFriendsData = userFriendsData;
        this.render();
        UIUtilsFriends.setCallbackFriendRequestResponded((target, isAccepted)=>this.onFriendRequestResponded(target, isAccepted));
    }
}
